import {
  deleteObject,
  getBlob as getStorageBlob,
  getDownloadURL as getStorageDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import type { FirebaseStorage } from 'firebase/storage';

import { storage as defaultStorage } from '@/firebase-config';
import { firebaseFunctionCommands } from '../commands/firebase-function-commands';
import { BlobObjectNotFoundError } from '../contracts';
import type {
  BlobCleanupRequest,
  BlobDownloadUrlRequest,
  BlobListRequest,
  BlobReadRequest,
  BlobStoreClient,
  BlobUploadTarget,
  CommandClient,
  CreateUploadSessionRequest,
  FileAccessRequest,
  FileAccessUrl,
  FileRecord,
  FinalizeUploadRequest,
  UploadProgress,
  UploadSession,
  UploadTask,
} from '../contracts';

type StorageError = Error & { code?: string };

function stripLegacyFileUrls(file: FileRecord): FileRecord {
  const {
    url: _url,
    downloadUrl: _downloadUrl,
    ...sanitizedFile
  } = file as FileRecord & { downloadUrl?: unknown };

  return sanitizedFile as FileRecord;
}

function requireString(value: unknown, fieldName: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Blob store response missing ${fieldName}`);
  }

  return value;
}

function requireNumber(value: unknown, fieldName: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Blob store response missing ${fieldName}`);
  }

  return value;
}

function isStorageObjectNotFound(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && (error as StorageError).code === 'storage/object-not-found';
}

function throwNormalizedStorageObjectError(error: unknown, storagePath: string): never {
  if (isStorageObjectNotFound(error)) {
    throw new BlobObjectNotFoundError(storagePath);
  }

  throw error;
}

function uploadMetadata(target: BlobUploadTarget, file: File): Record<string, string> {
  const metadata = { ...(target.customMetadata ?? {}) };

  if ('fileId' in target && typeof target.fileId === 'string') {
    metadata.uploadId = target.id;
    metadata.fileId = target.fileId;
    metadata.originalName = file.name;
  }

  if ('versionNumber' in target && typeof target.versionNumber === 'number') {
    metadata.versionNumber = target.versionNumber.toString();
  }

  return metadata;
}

export class FirebaseBlobStoreClient implements BlobStoreClient {
  private readonly commands: CommandClient;
  private readonly storage?: FirebaseStorage;

  constructor(
    commands: CommandClient,
    storage?: FirebaseStorage,
  ) {
    this.commands = commands;
    this.storage = storage;
  }

  private storageClient(): FirebaseStorage {
    return this.storage ?? defaultStorage;
  }

  async createUploadSession(request: CreateUploadSessionRequest): Promise<UploadSession> {
    const response = await this.commands.call(
      firebaseFunctionCommands.createProjectFileUploadSession,
      {
        orgId: request.scope.orgId,
        projectId: request.scope.projectId,
        name: request.fileName,
        mimeType: request.contentType,
        size: request.byteSize,
      },
    );

    return {
      id: requireString(response.uploadId, 'uploadId'),
      fileId: requireString(response.fileId, 'fileId'),
      storagePath: requireString(response.storagePath, 'storagePath'),
      versionNumber: requireNumber(response.versionNumber, 'versionNumber'),
    };
  }

  upload(target: BlobUploadTarget, file: File, options: { signal?: AbortSignal } = {}): UploadTask {
    const storageRef = ref(this.storageClient(), target.storagePath);
    const firebaseTask = uploadBytesResumable(storageRef, file, {
      contentType: target.contentType ?? file.type,
      customMetadata: uploadMetadata(target, file),
    });

    const progressListeners = new Set<(progress: UploadProgress) => void>();
    let settled = false;
    const finished = new Promise<void>((resolve, reject) => {
      const unsubscribe = firebaseTask.on(
        'state_changed',
        (snapshot) => {
          const progress = {
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
          };

          for (const listener of progressListeners) {
            listener(progress);
          }
        },
        (error) => {
          settled = true;
          unsubscribe();
          reject(error instanceof Error ? error : new Error(String(error)));
        },
        () => {
          settled = true;
          unsubscribe();
          resolve();
        },
      );
    });

    if (options.signal) {
      const abort = () => {
        if (!settled) {
          firebaseTask.cancel();
        }
      };
      const removeAbortListener = () => options.signal?.removeEventListener('abort', abort);

      if (options.signal.aborted) {
        abort();
      } else {
        options.signal.addEventListener('abort', abort, { once: true });
      }
      void finished.then(removeAbortListener, removeAbortListener);
    }

    return {
      cancel: () => firebaseTask.cancel(),
      finished,
      onProgress: (listener) => {
        progressListeners.add(listener);
        return () => progressListeners.delete(listener);
      },
    };
  }

  async finalizeUpload(request: FinalizeUploadRequest): Promise<FileRecord> {
    const response = await this.commands.call(
      firebaseFunctionCommands.finalizeProjectFileUpload,
      {
        orgId: request.scope.orgId,
        projectId: request.scope.projectId,
        fileId: request.fileId,
        uploadId: request.sessionId,
        storagePath: request.storagePath,
        versionNumber: request.versionNumber,
        parentId: request.parentId,
        name: request.fileName,
        mimeType: request.contentType,
        size: request.byteSize,
        policy: request.policy,
      },
    );

    if (!response.file) {
      throw new Error('Blob store response missing file');
    }

    return stripLegacyFileUrls(response.file);
  }

  async getAccessUrl(request: FileAccessRequest): Promise<FileAccessUrl> {
    const response = await this.commands.call(
      firebaseFunctionCommands.requestProjectFileAccess,
      {
        orgId: request.scope.orgId,
        projectId: request.scope.projectId,
        fileId: request.fileId,
        action: request.action ?? 'access',
        ...(request.reason ? { reason: request.reason } : {}),
        ...(request.storageRef ? { storageRef: request.storageRef } : {}),
      },
    );

    const access: FileAccessUrl = {
      url: requireString(response.url, 'url'),
      expiresAt: requireNumber(response.expiresAt, 'expiresAt'),
    };

    if (response.file) {
      access.file = stripLegacyFileUrls(response.file);
    }
    if (typeof response.contentType === 'string') {
      access.contentType = response.contentType;
    }

    return access;
  }

  async getBlob(request: BlobReadRequest): Promise<Blob> {
    try {
      return await getStorageBlob(ref(this.storageClient(), request.storagePath));
    } catch (error: unknown) {
      throwNormalizedStorageObjectError(error, request.storagePath);
    }
  }

  async getDownloadUrl(request: BlobDownloadUrlRequest): Promise<string> {
    try {
      return await getStorageDownloadURL(ref(this.storageClient(), request.storagePath));
    } catch (error: unknown) {
      throwNormalizedStorageObjectError(error, request.storagePath);
    }
  }

  async list(request: BlobListRequest): Promise<string[]> {
    try {
      const result = await listAll(ref(this.storageClient(), request.prefix));
      return result.items.map(item => item.fullPath);
    } catch (error: unknown) {
      throwNormalizedStorageObjectError(error, request.prefix);
    }
  }

  async cleanup(request: BlobCleanupRequest): Promise<void> {
    try {
      await deleteObject(ref(this.storageClient(), request.storagePath));
    } catch (error) {
      if (isStorageObjectNotFound(error)) {
        if (request.ignoreMissing) {
          return;
        }

        throwNormalizedStorageObjectError(error, request.storagePath);
      }

      throw error;
    }
  }
}
